<template>
  <v-card outlined class="display-settings">
    <v-card-title class="tw-text-base tw-font-bold tw-pb-1">การแสดงผล</v-card-title>
    <v-card-text>
      <div v-if="isLoading" class="tw-py-4 tw-flex tw-justify-center">
        <v-progress-circular :size="24" color="primary" indeterminate />
      </div>
      <template v-else>
        <div class="section-label">ธนาคารที่แสดงในหน้าอัตราแลกเปลี่ยน</div>
        <p class="tw-text-xs tw-text-gray-600 tw-mb-2">ต้องเลือกอย่างน้อย 1 ธนาคาร</p>
        <div class="bank-grid">
          <div
            v-for="bank in banks"
            :key="bank.bankCode"
            :class="['option', { 'option--on': form.visibleBanks.includes(bank.bankCode) }]"
          >
            <v-checkbox
              v-model="form.visibleBanks"
              :value="bank.bankCode"
              hide-details
              dense
              class="tw-mt-0 tw-pt-0 tw-w-full"
              :disabled="isSaving"
            >
              <template v-slot:label>
                <span class="tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-text-gray-900">
                  <img :src="bank.bankLogoUrl" alt="" class="tw-h-6 tw-w-6 tw-rounded-full" />
                  <span class="tw-flex tw-flex-col tw-leading-tight tw-min-w-0">
                    <b>{{ bank.bankCode }}</b>
                    <span class="tw-text-xs tw-text-gray-600 bank-name">{{ bank.bankName }}</span>
                  </span>
                </span>
              </template>
            </v-checkbox>
          </div>
        </div>

        <div class="section-label tw-mt-6">เมนู</div>
        <div :class="['option', 'tw-mt-2', { 'option--on': form.showHistoryPage }]">
          <v-switch
            v-model="form.showHistoryPage"
            inset
            hide-details
            dense
            color="#1F7A52"
            class="tw-mt-0 tw-pt-0 tw-w-full"
            :disabled="isSaving"
          >
            <template v-slot:label>
              <span class="tw-flex tw-flex-col tw-leading-tight tw-text-gray-900">
                <b>แสดงหน้า "ประวัติอัตราแลกเปลี่ยน"</b>
                <span class="tw-text-xs tw-text-gray-600">ปิดแล้วเมนูจะหายไป และลิงก์เดิมจะพาไปหน้าอัตราแลกเปลี่ยนแทน</span>
              </span>
            </template>
          </v-switch>
        </div>
      </template>
      <v-alert v-if="error" type="error" dense text class="tw-mt-4 tw-mb-0">{{ error }}</v-alert>
    </v-card-text>
    <v-card-actions class="tw-px-4 tw-pb-4">
      <span v-if="savedMessage" class="tw-text-sm tw-text-gray-600">{{ savedMessage }}</span>
      <v-spacer />
      <v-btn
        depressed
        color="primary"
        height="44"
        :loading="isSaving"
        :disabled="isLoading || !form.visibleBanks.length || !isChanged"
        @click="save"
      >
        บันทึก
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { EXCHANGE_BANKS, visibleBanks, historyPageEnabled } from "@/libs/exchangeRateExport";

function sameForm(a, b) {
  return (
    a.showHistoryPage === b.showHistoryPage &&
    a.visibleBanks.slice().sort().join() === b.visibleBanks.slice().sort().join()
  );
}

export default {
  name: "ExchangeDisplaySettings",
  props: {
    companyId: { type: String, required: true },
  },
  data() {
    return {
      banks: EXCHANGE_BANKS,
      form: { visibleBanks: [], showHistoryPage: true },
      saved: { visibleBanks: [], showHistoryPage: true },
      isLoading: true,
      isSaving: false,
      error: "",
      savedMessage: "",
    };
  },
  computed: {
    isChanged() {
      return !sameForm(this.form, this.saved);
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.savedMessage = "";
      },
    },
  },
  async mounted() {
    try {
      const setting = await this.$store.dispatch("exchangeRate/loadPageSetting", {
        companyId: this.companyId,
        force: true,
      });
      this.saved = {
        visibleBanks: visibleBanks(setting.visibleBanks).map((b) => b.bankCode),
        showHistoryPage: historyPageEnabled(setting),
      };
      this.form = { visibleBanks: this.saved.visibleBanks.slice(), showHistoryPage: this.saved.showHistoryPage };
    } catch (e) {
      this.error = (e && e.message) || String(e);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async save() {
      this.isSaving = true;
      this.error = "";
      try {
        // Stored in the page's order so the setting reads the same way back.
        const patch = {
          visibleBanks: this.banks.map((b) => b.bankCode).filter((code) => this.form.visibleBanks.includes(code)),
          showHistoryPage: this.form.showHistoryPage,
        };
        await this.$store.dispatch("exchangeRate/savePageSetting", { companyId: this.companyId, patch });
        this.saved = { visibleBanks: patch.visibleBanks.slice(), showHistoryPage: patch.showHistoryPage };
        this.form = { visibleBanks: patch.visibleBanks.slice(), showHistoryPage: patch.showHistoryPage };
        this.$nextTick(() => {
          this.savedMessage = "บันทึกแล้ว";
        });
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.display-settings {
  border-radius: 12px !important;
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #1a1d23;
}

.bank-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
}

.option {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 6px 12px;
  border: 1px solid #e3e6eb;
  border-radius: 10px;
}

/* Vuetify's checkbox/switch wrappers don't shrink below their label, so a
   long bank name (BCEL's) pushed past the card. Let them shrink and the name
   wrap instead of overflowing. */
.option >>> .v-input__control,
.option >>> .v-input__slot,
.option >>> .v-label {
  min-width: 0;
}

.bank-name {
  white-space: normal;
  overflow-wrap: anywhere;
}

.option--on {
  border-color: #1f7a52;
  background: #f3faf6;
}
</style>
